import { ArrowLeft, ArrowRight } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Location, LocationDescriptor } from 'history';
import { Interval } from 'luxon';
import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { PageHeaderContainer, PageHeading } from 'common/components/PageHeader';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { typographyCss } from 'common/typography';

import { InsightPageControls } from './InsightPageControl';
import { DateRange, InsightEntity } from '../types';

type InsightPageHeaderProps = {
  insightEntity: InsightEntity;
  dateInterval: Interval;
  dateRange: DateRange;
  title: ReactNode;
  backLinkText?: ReactNode;
  backLink?: string | ((location: Location) => LocationDescriptor);
  isVATEnabled: boolean;
  setIsVATEnabled: (isVATEnabled: boolean) => void;
  setDateRange: (dateRange: DateRange) => void;
};

const HeadingLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledInsightsLink = styled(Link)`
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${cbSemanticCssVars.textSecondary};
  cursor: pointer;
  margin-block-end: 4px;
`;

const BackLinkText = styled.span`
  ${typographyCss.Body3}
  font-weight: 500;
`;

const StyledArrowLeft = styled(ArrowLeft)`
  fill: ${cbSemanticCssVars.textSecondary};
`;

const StyledArrowRight = styled(ArrowRight)`
  fill: ${cbSemanticCssVars.textSecondary};
`;

const SmallerPageHeading = styled.h1`
  ${typographyCss.Heading5}
  margin: 0;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Heading6}
  }
`;

const InsightPageHeader = ({
  insightEntity,
  dateInterval,
  dateRange,
  title,
  isVATEnabled,
  backLinkText,
  backLink,
  setDateRange,
  setIsVATEnabled,
}: InsightPageHeaderProps) => {
  const { cbDirection } = useTheme();
  const isHeadingPrefixEnabled = backLink && backLinkText;
  const Heading = isHeadingPrefixEnabled ? SmallerPageHeading : PageHeading;

  return (
    <PageHeaderContainer>
      <HeadingLabelContainer>
        {isHeadingPrefixEnabled && (
          <StyledInsightsLink to={backLink}>
            {cbDirection === 'ltr' ? <StyledArrowLeft width="12px" /> : <StyledArrowRight width="12px" />}
            <BackLinkText>{backLinkText}</BackLinkText>
          </StyledInsightsLink>
        )}
        <Heading>{title}</Heading>
      </HeadingLabelContainer>
      <InsightPageControls
        insightEntity={insightEntity}
        dateRange={dateRange}
        dateInterval={dateInterval}
        isVATEnabled={isVATEnabled}
        setIsVATEnabled={setIsVATEnabled}
        setDateRange={setDateRange}
      />
    </PageHeaderContainer>
  );
};

export default InsightPageHeader;
